import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Edit3, AlertTriangle, Wand2 } from "lucide-react";
import MovementFixDialog from "@/components/MovementFixDialog";

/**
 * Full-screen drill-down for a single supplier's spend over the last N days.
 * Lists every IN movement contributing to total_spend, sorted by line_total
 * descending, so an outlier receive (a decimal-shift typo turning $57.25 into
 * $5,725 and inflating the supplier's total by two orders of magnitude) sits
 * at the top of the table.
 *
 * Manager can inline-edit any movement's unit_cost — the row's line_total
 * recomputes and the modal's total_spend updates in real time (no page reload
 * needed).
 */
const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";

const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SupplierSpendDetailDialog = ({
  open,
  supplierId,
  supplierName,
  initialDays = 90,
  onClose,
  onFixed,
}) => {
  const [days, setDays] = useState(initialDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixMovement, setFixMovement] = useState(null); // movement being edited

  // Keep the local `days` in sync when caller changes initialDays (e.g. user
  // toggled the Reports days selector, then opened another supplier).
  useEffect(() => {
    setDays(initialDays);
  }, [initialDays, supplierId]);

  const load = async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const resp = await api.get(`/reports/supplier/${supplierId}`, {
        params: { days },
      });
      setData(resp.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load supplier spend");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, supplierId, days]);

  // Live-computed total from the current (possibly-edited) movements array so
  // the header total reflects inline fixes before the next load. Includes
  // companion PO totals so processor rows (JV Strappings et al.) match the
  // parent Reports summary.
  const liveTotal = useMemo(() => {
    if (!data) return 0;
    const movs = (data.movements || []).reduce(
      (s, m) => s + (Number(m.line_total) || 0),
      0,
    );
    const comps = (data.companion_pos || []).reduce(
      (s, c) => s + (Number(c.line_total) || 0),
      0,
    );
    return movs + comps;
  }, [data]);

  const outlierThreshold = useMemo(() => {
    if (!data?.movements?.length) return null;
    // Median-based outlier flag: any movement whose line_total > 5× median flags red.
    const sorted = [...data.movements]
      .map((m) => Number(m.line_total) || 0)
      .sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median =
      sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    if (median <= 0) return null;
    return median * 5;
  }, [data]);

  const startEdit = (m) => setFixMovement(m);

  const applyFix = (updated) => {
    // If the movement was soft-deleted, drop it from the drill-down list
    // immediately — the backend endpoint already excludes deleted movements,
    // and keeping it visible with `line-through` was confusing (page looked
    // "stuck" until the user navigated away and back).
    if (updated?.deleted) {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          movements: prev.movements.filter((r) => r.id !== updated.id),
        };
      });
      // Reload from server so total_spend / count stay honest.
      load();
      onFixed?.();
      return;
    }
    // Update local state so the row + total refresh without a full reload.
    setData((prev) => {
      if (!prev) return prev;
      const movements = prev.movements.map((r) =>
        r.id === updated.id
          ? {
              ...r,
              qty: updated.qty ?? r.qty,
              unit_cost: updated.unit_cost ?? r.unit_cost,
              line_total: Number(
                (
                  Number(updated.unit_cost ?? r.unit_cost) *
                  Number(updated.qty ?? r.qty)
                ).toFixed(2),
              ),
            }
          : r,
      );
      return { ...prev, movements };
    });
    onFixed?.();
  };

  const zeroCostRows = useMemo(
    () =>
      (data?.movements || []).filter(
        (r) => !r.unit_cost || Number(r.unit_cost) === 0,
      ),
    [data],
  );

  const backfillZeros = async () => {
    if (
      !window.confirm(
        `Backfill unit_cost from Inventory for ${zeroCostRows.length} movement(s) currently at $0? This uses each item's current unit_cost. Items still at $0 in Inventory will be skipped.`,
      )
    )
      return;
    try {
      const { data: res } = await api.post(
        `/reports/supplier/${supplierId}/backfill-zero-costs`,
      );
      toast.success(
        `Backfilled ${res.fixed} movement(s)`,
        res.skipped_no_cost
          ? { description: `${res.skipped_no_cost} skipped (item still has no cost).` }
          : undefined,
      );
      await load();
      onFixed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="supplier-spend-detail-dialog"
    >
      <div className="bg-white border-4 border-black rounded-3xl shadow-[8px_8px_0px_0px_#000] w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b-2 border-black bg-gradient-to-r from-[#0D1F3C] to-[#1E3C66] text-white rounded-t-[22px] flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow opacity-80">Spend detail</div>
            <div className="font-display text-3xl leading-tight">
              {supplierName || "Supplier"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              Every IN movement contributing to this supplier&apos;s total in the last {days} days,
              sorted by line total. Pencil to correct any wrong snapshot.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="spend-detail-days"
              className="nb-input py-2 text-sm text-black"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
              <option value={365}>Last 365 days</option>
              <option value={3650}>All time</option>
            </select>
            <button
              data-testid="spend-detail-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
              aria-label="close"
            >
              <X size={16} strokeWidth={2.5} className="text-black" />
            </button>
          </div>
        </div>

        {/* Total headline */}
        <div className="p-5 border-b-2 border-black bg-[#DFEEF7] flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow">Total spend ({days}d)</div>
            <div
              data-testid="spend-detail-total"
              className="font-mono-num font-bold text-4xl"
            >
              {money(liveTotal)}
            </div>
          </div>
          <div className="text-xs text-[#607087] max-w-md text-right">
            Sum of qty × unit_cost snapshot on every IN movement below. Fixing a wrong
            snapshot here also fixes the Reports → By supplier number instantly.
          </div>
        </div>

        {/* Backfill nudge — only visible when at least one $0 row exists */}
        {zeroCostRows.length > 0 && (
          <div
            data-testid="backfill-zero-costs-notice"
            className="px-5 py-3 border-b-2 border-[#C7A43B] bg-[#FFF7DB] flex items-center justify-between gap-3 flex-wrap"
          >
            <div className="text-xs text-[#5B4500] flex items-start gap-2">
              <AlertTriangle size={14} strokeWidth={2.5} className="mt-0.5 shrink-0" />
              <div>
                {zeroCostRows.length} movement{zeroCostRows.length === 1 ? "" : "s"} received with a $0 snapshot.
                If you&apos;ve since set the item&apos;s Inventory cost, tap Backfill to stamp
                that current cost onto these historical rows. Manual edits above are
                preserved.
              </div>
            </div>
            <button
              data-testid="backfill-zero-costs-btn"
              onClick={backfillZeros}
              className="nb-btn nb-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5"
            >
              <Wand2 size={12} strokeWidth={2.5} />
              Backfill {zeroCostRows.length} row{zeroCostRows.length === 1 ? "" : "s"}
            </button>
          </div>
        )}

        {/* Movements table */}
        <div className="flex-1 overflow-y-auto scrollbar-clean">
          {loading && (
            <div className="p-10 text-center text-[#607087]">Loading…</div>
          )}
          {/* Companion PO block — for processors (JV Strappings etc.) whose
              spend comes from service POs rather than stock receives. */}
          {!loading && (data?.companion_pos?.length || 0) > 0 && (
            <div data-testid="spend-detail-companion-block" className="border-b-2 border-black">
              <div className="px-4 py-2 bg-[#FFF8E1] border-b border-[#E5A72E] text-xs flex items-center gap-2">
                <span className="text-[10px] font-black tracking-wider uppercase bg-[#FFE9C2] border border-[#7A5300] text-[#7A5300] rounded px-1.5 py-0.5">
                  Toll processing
                </span>
                <span className="text-[#7A5300]">
                  {data.companion_pos.length} cutting / service PO{data.companion_pos.length !== 1 ? "s" : ""}
                  {" · "}
                  <b className="font-mono-num">{money(data.toll_processing_total)}</b>
                </span>
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                  <div className="grid grid-cols-12 gap-2 p-3 border-b border-black/10 label-eyebrow bg-[#FFFCF2]">
                    <div className="col-span-3">Service</div>
                    <div className="col-span-2">Sent / created</div>
                    <div className="col-span-1 text-right">Qty</div>
                    <div className="col-span-2">Fee / log</div>
                    <div className="col-span-2 text-right">Line total</div>
                    <div className="col-span-2">Linked to</div>
                  </div>
                  {data.companion_pos.map((c) => (
                    <div
                      key={c.id}
                      data-testid={`spend-detail-companion-${c.id}`}
                      className="grid grid-cols-12 gap-2 p-3 border-b border-black/10 items-center text-sm bg-[#FFFCF2]"
                    >
                      <div className="col-span-3 font-display truncate" title={c.description}>
                        {c.po_number}
                      </div>
                      <div className="col-span-2 font-mono-num text-xs">
                        {fmt(c.sent_at || c.created_at)}
                      </div>
                      <div className="col-span-1 font-mono-num text-right font-bold">
                        {c.qty} <span className="text-[10px] text-[#607087]">{c.unit}</span>
                      </div>
                      <div className="col-span-2 font-mono-num">{money(c.unit_cost)}</div>
                      <div className="col-span-2 font-mono-num text-right font-bold">
                        {money(c.line_total)}
                      </div>
                      <div className="col-span-2 text-xs text-[#7A5300] truncate" title={c.parent_supplier_name || ""}>
                        {c.parent_po_number || "—"}
                        {c.parent_supplier_name && (
                          <span className="block text-[10px] text-[#607087]">
                            {c.parent_supplier_name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {!loading &&
            (!data?.movements || data.movements.length === 0) &&
            (!data?.companion_pos || data.companion_pos.length === 0) && (
              <div className="p-10 text-center text-[#607087]">
                No spend recorded in this range.
              </div>
            )}
          {!loading && data?.movements?.length > 0 && (
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
              <div className="grid grid-cols-12 gap-2 p-3 border-b-2 border-black bg-[#F4F8FB] label-eyebrow sticky top-0">
                <div className="col-span-3">Item</div>
                <div className="col-span-2">When</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2">Unit cost</div>
                <div className="col-span-2 text-right">Line total</div>
                <div className="col-span-1">Batch</div>
                <div className="col-span-1">User</div>
              </div>
              {data.movements.map((m) => {
                const isOutlier =
                  outlierThreshold !== null &&
                  Number(m.line_total) > outlierThreshold;
                return (
                  <div
                    key={m.id}
                    data-testid={`spend-detail-row-${m.id}`}
                    className={`grid grid-cols-12 gap-2 p-3 border-b border-black/10 items-center text-sm ${
                      isOutlier ? "bg-[#FCE4E4]" : ""
                    }`}
                  >
                    <div className="col-span-3 font-display truncate" title={m.stock_name}>
                      {isOutlier && (
                        <AlertTriangle
                          size={12}
                          strokeWidth={2.5}
                          className="inline text-[#8A1F1F] mr-1 -mt-0.5"
                        />
                      )}
                      {m.stock_name}
                    </div>
                    <div className="col-span-2 font-mono-num text-xs">
                      {fmt(m.created_at)}
                    </div>
                    <div className="col-span-1 font-mono-num text-right font-bold">
                      {m.qty} <span className="text-[10px] text-[#607087]">{m.unit}</span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono-num">{money(m.unit_cost)}</span>
                        <button
                          data-testid={`spend-detail-cost-edit-${m.id}`}
                          onClick={() => startEdit(m)}
                          className="nb-btn bg-white p-1"
                          aria-label="fix movement"
                          title="Fix qty or unit cost"
                        >
                          <Edit3 size={10} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                    <div
                      className={`col-span-2 font-mono-num text-right font-bold ${
                        isOutlier ? "text-[#8A1F1F]" : ""
                      }`}
                    >
                      {money(m.line_total)}
                    </div>
                    <div className="col-span-1 text-xs text-[#607087] truncate">
                      {m.batch_ref || "—"}
                    </div>
                    <div className="col-span-1 text-xs truncate">
                      {m.user_name || "—"}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="p-3 border-t-2 border-black bg-[#F4F8FB] text-xs text-[#607087]">
          Rows in red are 5× the median line total — likely typos. Click the pencil to
          fix the qty or unit cost. Reports update instantly on save.
        </div>
      </div>

      <MovementFixDialog
        open={!!fixMovement}
        movement={fixMovement}
        onClose={() => setFixMovement(null)}
        onSaved={applyFix}
      />
    </div>
  );
};

export default SupplierSpendDetailDialog;
